// PublicPrompts.js - Prompts multilingues pour la génération de stratégies personnalisées

export const PublicPrompts = {
  fr: (userObjective) => `Objectif utilisateur: "${userObjective}"

Tu es un expert en stratégie et développement personnel/professionnel. Génère une stratégie personnalisée pour cet objectif spécifique, en trois étapes.

IMPORTANT: Tu DOIS répondre UNIQUEMENT avec un objet JSON valide, sans aucun texte avant ou après, sans backticks, sans commentaires. Utilise exactement cette structure :

{
  "titre": "Version reformulée et motivante de l'objectif utilisateur: ${userObjective}",
  "phases": [
    "Etape 1 spécifique à cet objectif avec action concrète",
    "Etape 2 adaptée à cet objectif avec résultat mesurable", 
    "Etape 3 personnalisée pour cet objectif avec impact"
  ],
  "ressources": [
    "Institution ou organisme expert spécialisé dans ce domaine précis",
    "Formation ou cours recommandé directement lié à cet objectif",
    "Plateforme ou outil professionnel recommandé pour cet objectif"
  ],
  "strategie": "Conseil stratégique personnalisé et actionnable pour cet objectif spécifique en 1-2 phrases"
}

IMPORTANT: Assure-toi que le titre reformule clairement l'objectif "${userObjective}" et que toute la stratégie est cohérente avec cet objectif précis.

Réponds UNIQUEMENT avec ce JSON, rien d'autre.`,

  en: (userObjective) => `User objective: "${userObjective}"

You are an expert in strategy and personal/professional development. Generate a personalized strategy for this specific objective, in three steps.

IMPORTANT: You MUST respond ONLY with a valid JSON object, without any text before or after, without backticks, without comments. Use exactly this structure:

{
  "titre": "Reformulated and motivating version of the user objective: ${userObjective}",
  "phases": [
    "Step 1 specific to this objective with concrete action",
    "Step 2 adapted to this objective with measurable result", 
    "Step 3 personalized for this objective with impact"
  ],
  "ressources": [
    "Institution or expert organization specialized in this precise domain",
    "Training or course recommended directly related to this objective",
    "Professional platform or tool recommended for this objective"
  ],
  "strategie": "Personalized and actionable strategic advice for this specific objective in 1-2 sentences"
}

IMPORTANT: Make sure the title clearly reformulates the objective "${userObjective}" and that the entire strategy is coherent with this precise objective.

Respond ONLY with this JSON, nothing else.`,

  es: (userObjective) => `Objetivo del usuario: "${userObjective}"

Eres un experto en estrategia y desarrollo personal/profesional. Genera una estrategia personalizada para este objetivo específico, en tres etapas.

IMPORTANTE: DEBES responder ÚNICAMENTE con un objeto JSON válido, sin ningún texto antes o después, sin backticks, sin comentarios. Usa exactamente esta estructura:

{
  "titre": "Versión reformulada y motivadora del objetivo del usuario: ${userObjective}",
  "phases": [
    "Etapa 1 específica para este objetivo con acción concreta",
    "Etapa 2 adaptada a este objetivo con resultado medible", 
    "Etapa 3 personalizada para este objetivo con impacto"
  ],
  "ressources": [
    "Institución u organismo experto especializado en este dominio preciso",
    "Formación o curso recomendado directamente relacionado con este objetivo",
    "Plataforma o herramienta profesional recomendada para este objetivo"
  ],
  "strategie": "Consejo estratégico personalizado y accionable para este objetivo específico en 1-2 frases"
}

IMPORTANTE: Asegúrate de que el título reformule claramente el objetivo "${userObjective}" y que toda la estrategia sea coherente con este objetivo preciso.

Responde ÚNICAMENTE con este JSON, nada más.`,

  ar: (userObjective) => `هدف المستخدم: "${userObjective}"

أنت خبير في الاستراتيجية والتطوير الشخصي/المهني. قم بإنشاء استراتيجية مخصصة لهذا الهدف المحدد، في ثلاث مراحل.

مهم: يجب أن ترد فقط بكائن JSON صحيح، بدون أي نص قبل أو بعد، بدون backticks، بدون تعليقات. استخدم بالضبط هذا الهيكل:

{
  "titre": "نسخة معاد صياغتها ومحفزة من هدف المستخدم: ${userObjective}",
  "phases": [
    "المرحلة 1 محددة لهذا الهدف مع إجراء ملموس",
    "المرحلة 2 مكيفة مع هذا الهدف مع نتيجة قابلة للقياس", 
    "المرحلة 3 مخصصة لهذا الهدف مع التأثير"
  ],
  "ressources": [
    "مؤسسة أو منظمة خبيرة متخصصة في هذا المجال المحدد",
    "تدريب أو دورة موصى بها مرتبطة مباشرة بهذا الهدف",
    "منصة أو أداة مهنية موصى بها لهذا الهدف"
  ],
  "strategie": "نصيحة استراتيجية مخصصة وقابلة للتنفيذ لهذا الهدف المحدد في جملة أو جملتين"
}

مهم: تأكد من أن العنوان يعيد صياغة الهدف "${userObjective}" بوضوح وأن الاستراتيجية بأكملها متماسكة مع هذا الهدف المحدد.

أجب فقط بـ JSON هذا، لا شيء آخر.`
};

// Messages d'erreur localisés
export const demoErrorMessages = {
  fr: {
    invalidFormat: 'Format de réponse invalide',
    invalidFormatDesc: 'L\'IA a retourné une réponse dans un format incorrect. Veuillez réessayer.',
    incompleteStructure: 'Structure de réponse incomplète',
    incompleteStructureDesc: 'La réponse de l\'IA est incomplète. Veuillez réessayer.',
    noSteps: 'Aucune étape générée',
    noStepsDesc: 'L\'IA n\'a pas pu générer d\'étapes pour votre projet. Veuillez réessayer.',
    unexpected: 'Une erreur inattendue est survenue.',
    title: 'Une erreur est survenue'
  },
  en: {
    invalidFormat: 'Invalid response format',
    invalidFormatDesc: 'The AI returned a response in an incorrect format. Please try again.',
    incompleteStructure: 'Incomplete response structure',
    incompleteStructureDesc: 'The AI response is incomplete. Please try again.',
    noSteps: 'No steps generated',
    noStepsDesc: 'The AI could not generate steps for your project. Please try again.',
    unexpected: 'An unexpected error occurred.',
    title: 'An error occurred'
  },
  es: {
    invalidFormat: 'Formato de respuesta inválido',
    invalidFormatDesc: 'La IA devolvió una respuesta en formato incorrecto. Por favor, inténtalo de nuevo.',
    incompleteStructure: 'Estructura de respuesta incompleta',
    incompleteStructureDesc: 'La respuesta de la IA está incompleta. Por favor, inténtalo de nuevo.',
    noSteps: 'No se generaron etapas',
    noStepsDesc: 'La IA no pudo generar etapas para tu proyecto. Por favor, inténtalo de nuevo.',
    unexpected: 'Ocurrió un error inesperado.',
    title: 'Ocurrió un error'
  },
  ar: {
    invalidFormat: 'تنسيق الاستجابة غير صحيح',
    invalidFormatDesc: 'أعادت الذكاء الاصطناعي استجابة بتنسيق غير صحيح. يرجى المحاولة مرة أخرى.',
    incompleteStructure: 'هيكل الاستجابة غير مكتمل',
    incompleteStructureDesc: 'استجابة الذكاء الاصطناعي غير مكتملة. يرجى المحاولة مرة أخرى.',
    noSteps: 'لم يتم إنشاء خطوات',
    noStepsDesc: 'لم يتمكن الذكاء الاصطناعي من إنشاء خطوات لمشروعك. يرجى المحاولة مرة أخرى.',
    unexpected: 'حدث خطأ غير متوقع.',
    title: 'حدث خطأ'
  }
};

// Ressources de secours localisées
export const fallbackResources = {
  fr: {
    expert: 'Ressources expertes pour',
    training: 'Formation spécialisée en',
    platform: 'Plateforme d\'apprentissage pour'
  },
  en: {
    expert: 'Expert resources for',
    training: 'Specialized training in',
    platform: 'Learning platform for'
  },
  es: {
    expert: 'Recursos expertos para',
    training: 'Formación especializada en',
    platform: 'Plataforma de aprendizaje para'
  },
  ar: {
    expert: 'موارد الخبراء لـ',
    training: 'تدريب متخصص في',
    platform: 'منصة تعلم لـ'
  }
};

// Fonction utilitaire pour obtenir le prompt dans la langue appropriée
export const getLocalizedPrompt = (userObjective, language = 'fr') => {
  const supportedLanguages = ['fr', 'en', 'es', 'ar'];
  const lang = supportedLanguages.includes(language) ? language : 'fr';
  return PublicPrompts[lang](userObjective);
};

// Fonction utilitaire pour obtenir les messages d'erreur
export const getErrorMessages = (language = 'fr') => {
  const supportedLanguages = ['fr', 'en', 'es', 'ar'];
  const lang = supportedLanguages.includes(language) ? language : 'fr';
  return demoErrorMessages[lang];
};

// Fonction utilitaire pour obtenir les ressources de secours
export const getFallbackResources = (language = 'fr') => {
  const supportedLanguages = ['fr', 'en', 'es', 'ar'];
  const lang = supportedLanguages.includes(language) ? language : 'fr';
  return fallbackResources[lang];
};